import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAllPlannedWorkouts, getPlanOverview, refreshPlanPreview } from "@/api/plan";
import { getProfile } from "@/api/profile";
import { Button } from "@/components/ui/Button";
import { Card, SectionLabel } from "@/components/ui/Card";
import { PlanningQualityNotice } from "@/components/plan/PlanningQualityNotice";
import { GoalAssessmentCard } from "@/components/plan/GoalAssessmentCard";
import { WeekCard } from "@/components/plan/WeekCard";
import { CalendarView, type CalendarWorkout } from "@/components/plan/CalendarView";
import { addCalendarDays, calendarDateInTimezone, fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

type Filter = "all" | "upcoming" | "checkpoint";

export default function PlanOverview() {
  const { t } = useTranslation();
  const FILTERS = [
    { id: "all" as const, label: t("plan.filter_all") },
    { id: "upcoming" as const, label: t("plan.filter_upcoming") },
    { id: "checkpoint" as const, label: t("plan.filter_checkpoint") },
  ];
  const [filter, setFilter] = useState<Filter>("all");
  const [mode, setMode] = useState<"list" | "calendar">("list");

  const qc = useQueryClient();
  const refresh = useMutation({ mutationFn: refreshPlanPreview,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan"] });
      qc.invalidateQueries({ queryKey: ["plan-workouts"] });
      qc.invalidateQueries({ queryKey: ["week"] });
    },
  });
  const planQ = useQuery({ queryKey: ["plan"], queryFn: getPlanOverview });
  const profileQ = useQuery({ queryKey: ["profile"], queryFn: getProfile });
  const workoutsQ = useQuery({
    queryKey: ["plan-workouts"],
    queryFn: getAllPlannedWorkouts,
    enabled: mode === "calendar",
  });

  const calendarWorkouts = useMemo<CalendarWorkout[]>(() => {
    if (!workoutsQ.data) return [];
    return workoutsQ.data.map((w) => ({
      id: w.id,
      date: w.date,
      sport: w.sport,
      title: w.title,
      duration_min: w.duration_minutes,
      tss: w.target_tss,
      status: w.status,
    }));
  }, [workoutsQ.data]);

  if (planQ.isLoading) {
    return <div className="py-10 text-center text-taupe">{t("plan.loading_plan")}</div>;
  }
  if (!planQ.data) {
    return <div className="py-10 text-center text-coral">{t("plan.not_found")}</div>;
  }
  const data = planQ.data;

  const today = calendarDateInTimezone(profileQ.data?.timezone ?? "UTC");
  const currentWeek = data.weeks.find(
    (w) =>
      w.week_start_date <= today &&
      today <= addCalendarDays(w.week_start_date, 6),
  );

  const filtered = data.weeks.filter((w) => {
    if (filter === "upcoming") return addCalendarDays(w.week_start_date, 6) >= today;
    if (filter === "checkpoint") return [12, 20].includes(w.week_number);
    return true;
  });

  const phases = [...new Set(data.weeks.map((w) => w.phase))];

  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>{t("planning_quality.plan_title", { count: data.total_weeks })}</SectionLabel>
        <p className="mt-1 text-sm text-taupe">{t("race_dates.plan_period")}</p>
        <h1 className="mt-1 font-serif text-[28px] font-medium tracking-tight">
          {data.start_date && data.end_date
            ? t("plan.from_to", { from: fmtDate(data.start_date, "d MMM yyyy"), to: fmtDate(data.end_date, "d MMM yyyy") })
            : t("plan.empty_msg")}
        </h1>
        {data.start_date && data.end_date ? <p className="mt-2 text-sm text-taupe">{t("race_dates.plan_period_note")}</p> : null}
        {profileQ.data ? (
          <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <dt className="text-taupe">{t("race_dates.main_date")}</dt>
            <dd>{profileQ.data.target_race_date ? fmtDate(profileQ.data.target_race_date, "d MMMM yyyy") : "—"}</dd>
            <dt className="text-taupe">{t("race_dates.next_date")}</dt>
            <dd>{profileQ.data.next_race?.date ? fmtDate(profileQ.data.next_race.date, "d MMMM yyyy") : "—"}</dd>
          </dl>
        ) : null}
      </div>

      {!planQ.isError && <GoalAssessmentCard assessment={data.goal_assessment} />}

      <PlanningQualityNotice
        provisional={data.weeks.some((week) => week.provisional)}
        notes={data.weeks.flatMap((week) => week.planning_notes || [])}
        healthPolicy={data.current_health_policy}
      />
      {data.weeks.some((week) => week.provisional) && !data.current_health_policy?.block_automatic_training ? (
        <div>
          <Button variant="secondary" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
            {t("planning_quality.refresh_preview")}
          </Button>
          {refresh.isError ? <p role="alert" className="mt-2 text-sm text-coral">{t("planning_quality.refresh_failed")}</p> : null}
        </div>
      ) : null}

      <Card>
        <SectionLabel>{t("plan.phases")}</SectionLabel>
        <div className="mt-3 flex flex-wrap gap-2">
          {phases.map((p) => (
            <span
              key={p}
              className="rounded-full border border-cream-border bg-cream-light px-3 py-1 text-[12px] font-medium text-navy"
            >
              {p}
            </span>
          ))}
        </div>
      </Card>

      {/* View mode toggle */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex rounded-full border border-cream-border bg-cream-light p-0.5">
          {(["list", "calendar"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-[13px] transition",
                mode === m
                  ? "bg-navy text-cream-light"
                  : "text-taupe hover:text-navy",
              )}
            >
              {m === "list" ? t("plan.view_list") : t("plan.view_calendar")}
            </button>
          ))}
        </div>

        {mode === "list" ? (
          <div className="flex gap-1.5 overflow-x-auto">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "whitespace-nowrap rounded-full border px-3 py-1 text-[12px] transition",
                  filter === f.id
                    ? "border-amber bg-amber/15 text-amber-dark"
                    : "border-cream-border bg-cream-light text-taupe hover:text-navy",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {mode === "list" ? (
        <div className="space-y-2.5">
          {filtered.map((w) => (
            <WeekCard
              key={w.week_number}
              week={w}
              isCurrent={w.week_number === currentWeek?.week_number}
            />
          ))}
        </div>
      ) : workoutsQ.isLoading ? (
        <div className="py-6 text-center text-[13px] text-taupe">
          {t("plan.loading_workouts")}
        </div>
      ) : (
        <CalendarView
          weeks={data.weeks.map((w) => ({
            weekNumber: w.week_number,
            startDate: w.week_start_date,
            phase: w.phase,
            targetTss: w.target_tss,
          }))}
          workouts={calendarWorkouts}
          linkPrefix="/workout/planned/"
          today={today}
          emptyMessage={t("plan.empty_msg")}
        />
      )}
    </div>
  );
}
